import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Voice-Leading Lab v1 (Phase 4 Tier 3) lexicon entries — 15 entries in the
 * `'voice-leading'` category (3 tool/sub-mode + 5 principles + 4 chord pairs +
 * 1 idiom picker + 2 feedback wrappers). The 16th entry — the cross-link — lives
 * in `lexicon-crosslink-entries.ts` (category `'crosslink'`).
 *
 * Authored by Bowie in the Voice-Leading Lab scope memo; locked verbatim — do
 * not edit copy without Bowie review.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8.7
 * Bowie scope memo: .claude/agent-memory/bowie/project_voice_leading_lab_v1_scope.md
 */
export const VOICE_LEADING_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───── Tool + sub-modes (3) ─────
  {
    id: 'voice-leading-lab',
    category: 'voice-leading',
    displayName: 'Voice-Leading Lab',
    displayNameShort: 'Voice-Lead',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'See and hear how the notes inside chords move from one chord to the next — smooth vs jumpy.',
      intermediate: 'Explore and identify voice-leading quality: common tones, stepwise motion, and the classic parallel-5ths trap.',
      advanced: 'Voice-leading analysis — common-tone preservation, stepwise motion, parallel-motion rules (idiom-aware).',
    },
    tagLabel: { beginner: 'note motion', intermediate: 'voice leading', advanced: 'voice-leading mechanics' },
  },
  {
    id: 'voice-leading-explore',
    category: 'voice-leading',
    displayName: 'Voice-Leading Explore',
    displayNameShort: 'Explore',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Pick two chords and watch how each note travels between them — play both versions and hear the difference.',
      intermediate: 'Compare a smooth and an awkward voicing of the same chord pair side by side — see and hear why one flows.',
      advanced: 'Free exploration of voicing quality: per-voice motion breakdown, idiom toggle, and the score that ranks smoothness.',
    },
    tagLabel: { beginner: 'compare voicings', intermediate: 'explore voicings', advanced: 'voicing comparison' },
  },
  {
    id: 'voice-leading-quiz',
    category: 'voice-leading',
    displayName: 'Voice-Leading Quiz',
    displayNameShort: 'Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear two versions of a chord change and pick the one that sounds smoother.',
      intermediate: 'Listen to two voicings of the same chord pair and identify which one has smoother voice leading.',
      advanced: 'Ear-based smoothness discrimination — 10 questions across the chord-pair pool under the chosen idiom.',
    },
    tagLabel: { beginner: 'which is smoother', intermediate: 'smoothness quiz', advanced: 'voice-leading quiz' },
  },

  // ───── Principle entries (5) ─────
  {
    id: 'vl-common-tone',
    category: 'voice-leading',
    displayName: 'Common tone',
    displayNameShort: 'Common tone',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A note shared by both chords — keep it in the same voice and the music barely moves.',
      intermediate: 'Common tone — a pitch present in both chords. Holding it in the same voice is the smoothest possible motion.',
      advanced: 'Common-tone preservation — a shared chord-tone retained in the same voice across both chords. The highest-weighted smoothness principle.',
    },
    tagLabel: { beginner: 'shared note', intermediate: 'common tone', advanced: 'held common tone' },
  },
  {
    id: 'vl-stepwise',
    category: 'voice-leading',
    displayName: 'Stepwise motion',
    displayNameShort: 'Stepwise',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'When a note can’t stay put, the smoothest move is to the next note up or down.',
      intermediate: 'Stepwise motion — moving a voice by a half or whole step (≤ 2 semitones). The next-best option after holding a common tone.',
      advanced: 'Stepwise motion (m2 or M2) — conjunct voice motion that preserves melodic continuity and audibility of each line.',
    },
    tagLabel: { beginner: 'small move', intermediate: 'stepwise', advanced: 'conjunct motion' },
  },
  {
    id: 'vl-inner-leap',
    category: 'voice-leading',
    displayName: 'Inner-voice leap',
    displayNameShort: 'Inner leap',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'When a middle note jumps a long way, the chord change sounds jumpy — usually avoided.',
      intermediate: 'Inner-voice leap — a middle voice moving more than a step. Penalized more than outer-voice leaps, which define root motion.',
      advanced: 'Inner-voice leap (> 2 semitones) — disjunct motion in a non-outer voice. Penalized heavily; inner voices should glide while outer voices carry leaps.',
    },
    tagLabel: { beginner: 'big jump', intermediate: 'inner leap', advanced: 'inner-voice disjunction' },
  },
  {
    id: 'vl-parallel-fifths',
    category: 'voice-leading',
    displayName: 'Parallel fifths',
    displayNameShort: 'Parallel 5ths',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'When two voices move together by the same big interval (a 5th), the music sounds hollow — avoided in classical writing.',
      intermediate: 'Parallel fifths — two voices moving in the same direction by a perfect 5th. Forbidden in classical voice leading; common and accepted in jazz/pop.',
      advanced: 'Parallel perfect 5ths and 8vas — the classical voice-leading prohibition. The two voices fuse perceptually into a single voice, losing independence. Idiom-aware: classical/jazz/pop toggle controls flagging.',
    },
    tagLabel: { beginner: 'avoid in classical', intermediate: 'parallel 5ths', advanced: 'classical prohibition' },
  },
  {
    id: 'vl-contrary-motion',
    category: 'voice-leading',
    displayName: 'Contrary motion',
    displayNameShort: 'Contrary',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'When the top and bottom notes move in opposite directions, the chord change sounds especially balanced.',
      intermediate: 'Contrary motion — the outer voices (bass + top) moving in opposite directions. A stylistic bonus for smoothness.',
      advanced: 'Contrary outer-voice motion — soprano and bass moving in opposite directions. Maximizes voice independence; a smoothness bonus, not a hard rule.',
    },
    tagLabel: { beginner: 'opposite directions', intermediate: 'contrary motion', advanced: 'contrary outer motion' },
  },

  // ───── Pair entries (4) ─────
  {
    id: 'vl-pair-i-v',
    category: 'voice-leading',
    displayName: 'I → V voice leading',
    displayNameShort: 'I → V',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Going from the home chord to the tense chord — keep the shared note still and let the others step.',
      intermediate: 'I → V — the 5th of I (G in C major) is the root of V, so hold it as a common tone while the others step down.',
      advanced: 'I → V — common tone (ˆ5) retained in the top voice; inner and bass voices descend by step. The awkward voicing leaps all voices up, exposing parallel 5ths.',
    },
    tagLabel: { beginner: 'home to tense', intermediate: 'I → V', advanced: 'tonic to dominant' },
  },
  {
    id: 'vl-pair-i-iv',
    category: 'voice-leading',
    displayName: 'I → IV voice leading',
    displayNameShort: 'I → IV',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Going from home to the warm pre-tension chord — the root stays put while the others step up.',
      intermediate: 'I → IV — the root of I (C) is the 5th of IV, so hold it in the bass while the upper voices step up.',
      advanced: 'I → IV — common tone (ˆ1) retained in the bass; upper voices ascend by step. The awkward voicing leaps every voice upward.',
    },
    tagLabel: { beginner: 'home to warm', intermediate: 'I → IV', advanced: 'tonic to subdominant' },
  },
  {
    id: 'vl-pair-v-i',
    category: 'voice-leading',
    displayName: 'V → I (the cadence)',
    displayNameShort: 'V → I',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The classic “going home” move — the bass jumps down to the root while the tense note resolves up a half step.',
      intermediate: 'V → I — the authentic cadence. The leading tone (B in C major) resolves up to the tonic; the bass falls a 5th, which is the cadence’s defining motion (no penalty).',
      advanced: 'V → I — leading-tone resolution (ˆ7 → ˆ1) plus cadential bass (P5↓ / P4↑, exempt from leap penalty) and contrary outer motion. Common tone sacrificed to prioritize the cadence.',
    },
    tagLabel: { beginner: 'going home', intermediate: 'V → I cadence', advanced: 'authentic cadence' },
  },
  {
    id: 'vl-pair-iv-v',
    category: 'voice-leading',
    displayName: 'IV → V (the parallel-5ths trap)',
    displayNameShort: 'IV → V',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'These two chords share no notes, so it’s easy to slip into the hollow parallel-5ths sound — invert the first chord to dodge it.',
      intermediate: 'IV → V — no common tones. Root-position voicing creates parallel 5ths (classical no-no); putting IV in first inversion avoids them.',
      advanced: 'IV → V — the canonical parallel-5ths trap. Disjoint chords; root-position voicing yields parallel perfect 5ths (penalized in classical, accepted in jazz/pop). First-inversion IV (ˆ6 in bass) breaks the parallels.',
    },
    tagLabel: { beginner: 'hollow trap', intermediate: 'IV → V', advanced: 'parallel-5ths trap' },
  },

  // ───── Idiom picker (1) ─────
  {
    id: 'vl-idiom-picker',
    category: 'voice-leading',
    displayName: 'Idiom',
    displayNameShort: 'Idiom',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Classical music avoids certain note movements; jazz and pop allow them. Switch to hear the difference.',
      intermediate: 'Idiom — classical flags parallel 5ths/8vas as errors; jazz and pop accept them. The toggle changes how voicings are scored.',
      advanced: 'Idiom selector — toggles the parallel-perfect-interval penalty. Classical penalizes parallels (−10 each); jazz/pop ignore them. Default classical (pedagogical convention).',
    },
    tagLabel: { beginner: 'style', intermediate: 'idiom', advanced: 'idiom scoring' },
  },

  // ───── Feedback wrappers (2) ─────
  {
    id: 'vl-explore-feedback',
    category: 'voice-leading',
    displayName: 'Voicing breakdown',
    displayNameShort: 'Breakdown',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Here’s how each note moved between the two chords, and why this version sounds the way it does.',
      intermediate: 'Voice-motion breakdown — common tones held, stepwise moves, leaps, and any parallel 5ths, with the resulting smoothness score.',
      advanced: 'Per-voice motion analysis — common-tone / step / leap classification, parallel-perfect detection, contrary-motion bonus, and the idiom-scored total.',
    },
    tagLabel: { beginner: 'how it moved', intermediate: 'voice breakdown', advanced: 'motion analysis' },
  },
  {
    id: 'vl-quiz-feedback',
    category: 'voice-leading',
    displayName: 'Quiz feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'See which version was smoother and why — look at how the notes moved in each.',
      intermediate: 'Feedback — reveals the smoother voicing and names the principle that decided it (common tone, stepwise motion, or parallels avoided).',
      advanced: 'Quiz feedback — reveals the accepted slot, renders both voicings, and annotates the discriminating principle from the score breakdown.',
    },
    tagLabel: { beginner: 'the answer', intermediate: 'quiz feedback', advanced: 'discriminator' },
  },
  // ───── In-editor voice-leading diff (2) — Track A surface, 2026-06-06 ─────
  // Reuses `vl-common-tone` + `vl-stepwise` for the held/step concepts; these
  // two add the surface intro + a neutral leap concept (`vl-inner-leap` is
  // scoped to the Lab's inner-voice scoring, not a general "leap").
  {
    id: 'voiceleading-diff',
    category: 'voice-leading',
    displayName: 'Voice motion',
    displayNameShort: 'Voice motion',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Shows the smoothest way two chords connect — the notes they share, and how the rest step or jump.',
      intermediate: 'Voice motion — the smoothest connection between two chords: common tones held, the other voices moving as little as possible.',
      advanced: 'Voice-leading diff — the minimal-motion mapping between two chords (common tones held; remaining voices assigned by least total displacement). A property of the chord pair, not of any one voicing.',
    },
    tagLabel: { beginner: 'voice motion', intermediate: 'voice motion', advanced: 'voice-leading diff' },
  },
  {
    id: 'voiceleading-leap',
    category: 'voice-leading',
    displayName: 'Leap',
    displayNameShort: 'Leap',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A bigger jump — this voice moves more than a step to reach the next chord.',
      intermediate: 'Leap — a voice moving by more than a step (≥ 3 semitones). Less smooth than holding a common tone or stepping.',
      advanced: 'Disjunct motion (≥ m3) — a leap breaks melodic continuity; smoother voice leading minimises both the count and size of leaps.',
    },
    tagLabel: { beginner: 'a jump', intermediate: 'leap', advanced: 'disjunct motion' },
  },
]
